import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FormProvider } from 'react-hook-form';

import Dialog from 'components/mui/Dialog';
import Stack from '@mui/material/Stack';
import FormField from './components/FormField';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgressWithBackdrop from 'components/mui/CircularProgressWithBackdrop';

import { useGlobalAccountFormContext } from './hooks/useGlobalAccountFormContext';
import useProviderForm from './hooks/useProviderForm';

import { globalAccountsConfig } from './config';
import { PaymentProviderName } from 'domain/paySettings/helpers';

type Props = {
  onClose: () => void;
  providerName: PaymentProviderName;
  providerId: string;
  updatingAccount: boolean;
  handleCreate: (accountData: any) => void;
  handleUpdate: (accountData: any) => void;
  selectedAccount: {
    id: string;
  };
  bankAccounts:
    | Array<{
        label: string;
        value: string;
      }>
    | [];
};

const DialogAddGlobalAccount: React.FC<Props> = ({
  onClose,
  handleCreate,
  handleUpdate,
  selectedAccount,
  bankAccounts,
  providerName,
  providerId,
  updatingAccount,
}) => {
  const { formatMessage } = useIntl();

  const { context, isLoading } = useGlobalAccountFormContext({ providerName, providerId, bankAccounts });

  const config = globalAccountsConfig[providerName];

  const onSubmit = (data) => {
    const isEdit = Boolean(selectedAccount);
    const action = isEdit ? handleUpdate : handleCreate;

    const payload = data;

    if (isEdit) {
      payload.id = selectedAccount.id;
    }

    action(payload);
  };

  const defaultValues = useMemo(() => {
    const values = {};

    config.fields.forEach((field) => {
      values[field.name] = selectedAccount?.[field.name] ?? field.defaultValue;
    });

    return values;
  }, [selectedAccount, config]);

  const form = useProviderForm(providerName, defaultValues, Boolean(selectedAccount));

  return (
    <FormProvider {...form}>
      <Dialog
        open
        onClose={onClose}
        onOk={form.handleSubmit(onSubmit)}
        okBtnProps={{
          disabled: !form.formState.isValid,
        }}
        okText={formatMessage({
          id: selectedAccount ? 'button.save' : 'button.create',
          defaultMessage: selectedAccount ? 'Save' : 'Create',
        })}
        cancelText={formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        title={formatMessage({
          id: selectedAccount
            ? 'configurations.company.paymentProviders.detailsModal.accountList.editModal.title'
            : 'configurations.company.paymentProviders.detailsModal.accountList.addModal.title',
          defaultMessage: selectedAccount ? 'Edit account' : 'Add new account',
        })}
      >
        {isLoading ? (
          <LinearProgress />
        ) : (
          <Stack spacing={2} pt={1}>
            <CircularProgressWithBackdrop isOpen={updatingAccount} />
            {config.fields.map((field) => (
              <FormField key={field.name} field={field} formContext={context} isEditMode={selectedAccount} />
            ))}
          </Stack>
        )}
      </Dialog>
    </FormProvider>
  );
};

export default DialogAddGlobalAccount;
